import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import type { RootState } from "../../app/store";
import { UserFavorite } from "../../types/types";
import { API_URL } from "../../config";

export const favoritesApi = createApi({
  reducerPath: "favorites",
  baseQuery: fetchBaseQuery({
    baseUrl: API_URL,
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).auth.token;
      if (token) headers.set("Authorization", `Bearer ${token}`);
      headers.set("Content-Type", "application/json");
      return headers;
    },
  }),
  tagTypes: ["Favorite"],
  endpoints: (builder) => ({
    // Phase 3 (EB-482): scoped to the caller server-side via the Auth0 `sub` (no `?user=` param).
    getFavorites: builder.query<UserFavorite[], void>({
      query: () => "favorite",
      providesTags: ["Favorite"],
    }),
    addFavorite: builder.mutation<UserFavorite, { item_id: string }>({
      query: (body) => ({
        url: "favorite",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Favorite"],
    }),
    /**
     * Favourite counts across every user, for the admin grid (EB-523).
     *
     * Admin-only server-side, unlike every other endpoint here, because it aggregates over all
     * users rather than the caller. Untagged deliberately: favouriting is a shopper action and this
     * is an admin report, so invalidating it on `Favorite` would refetch a whole-table scan every
     * time someone hit a heart on the storefront.
     */
    getFavoriteCounts: builder.query<Record<string, number>, void>({
      query: () => "favorite/counts",
    }),
    // Keyed by (sub, item_id), so removal is by item_id — not a surrogate row id.
    removeFavorite: builder.mutation<void, string>({
      query: (itemId) => ({
        url: `favorite/${itemId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Favorite"],
    }),
  }),
});

export const {
  useGetFavoritesQuery,
  useGetFavoriteCountsQuery,
  useAddFavoriteMutation,
  useRemoveFavoriteMutation,
} = favoritesApi;

export default favoritesApi;
